import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useDispatch, useStore } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { setAllGroupUser, setGroupUser } from "../../../redux/TaskGroupingSlice";
import { addAllBoard, searchCard } from "../../../redux/SearchSlice";
import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { useBoards } from "../../../reactQuery/boards";
import { setActiveBoards } from "../../../redux/ActiveBoardSlice";
import { useResponsive } from "../../../hooks/useResponsive";
import { useUsers } from "../../../reactQuery/users";
import { useSetMultipleParams } from "../../../hooks/useSetMultipleParams";
import {
  getActiveBoards,
  getFlattenedGroupingUsers,
} from "../utils/myTaskUtils";

export function useGroupingHeaderState(groupingUserFromRedux = []) {
  const dispatch = useDispatch();
  const store = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchParam, setSearchParam] = useCustomSearchParams("txt");
  const [userParams] = useCustomSearchParams("uid");
  const [setMultipleSearchParams] = useSetMultipleParams();
  const isMobile = useResponsive("down", "md");
  const { boards } = useBoards();
  const { users } = useUsers();

  const [inputValue, setInputValue] = useState("");
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [openSearchField, setOpenSearchField] = useState(false);

  const syncedUserParamRef = useRef(null);

  const oldBoard = searchParams.get("board");
  const oldDate = searchParams.get("date");
  const oldFrom = searchParams.get("from");
  const oldDays = searchParams.get("days");
  const oldTo = searchParams.get("to");

  const filteredUser = useMemo(
    () =>
      getFlattenedGroupingUsers(groupingUserFromRedux).filter(
        (user) => user.active
      ),
    [groupingUserFromRedux]
  );

  useEffect(() => {
    dispatch(
      setActiveBoards(
        getActiveBoards(boards?.data?.data?.responseData || [])
      )
    );
  }, [boards, dispatch]);

  useEffect(() => {
    const availableUsers = users?.data?.data?.responseData || [];

    if (!userParams || !availableUsers.length) {
      syncedUserParamRef.current = null;
      return;
    }

    if (syncedUserParamRef.current === userParams) {
      return;
    }

    userParams.split(",").forEach((userId) => {
      const matchedUser = availableUsers.find(
        (user) => user.user_id === Number(userId)
      );

      if (matchedUser) {
        dispatch(setGroupUser(matchedUser));
      }
    });

    syncedUserParamRef.current = userParams;
  }, [dispatch, userParams, users]);

  const removeSearchParams = useCallback(
    (paramsToRemove) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      paramsToRemove.forEach((param) => {
        nextSearchParams.delete(param);
      });
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    dispatch(setAllGroupUser([]));
    dispatch(addAllBoard([]));
    setInputValue("");
    removeSearchParams(["board", "uid"]);
  }, [dispatch, removeSearchParams]);

  const debouncedSearch = useRef(
    _.debounce((value) => {
      dispatch(searchCard(value));
      setSearchParam("txt", value);
    }, 300)
  ).current;

  useEffect(() => {
    setInputValue(searchParam || "");
    dispatch(searchCard(searchParam || ""));

    if (
      oldBoard ||
      userParams ||
      oldFrom ||
      oldDays ||
      oldTo ||
      oldDate ||
      searchParams.get("switch") === "true"
    ) {
      setIsVisible(true);
    }
  }, [dispatch, oldBoard, oldDate, oldDays, oldFrom, oldTo, searchParam, searchParams, userParams]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      dispatch(setAllGroupUser([]));
      dispatch(addAllBoard([]));
    };
  }, [debouncedSearch, dispatch]);

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value.trimStart();
      setInputValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleOpenSearchField = useCallback(() => {
    setOpenSearchField((currentValue) => !currentValue);
  }, []);

  const handleApplyFilter = useCallback(() => {
    const currentState = store.getState();
    const currentBoardSearch = currentState.search.boardSearch;
    const currentGroupingUser = currentState.groupingUser.allocationUsers;

    setMultipleSearchParams([
      {
        name: "board",
        value: currentBoardSearch.join(","),
      },
      {
        name: "uid",
        value: (currentGroupingUser[0]?.users || [])
          .map((item) => item.user_id)
          .join(","),
      },
    ]);
    setFilterDrawer(false);
  }, [setMultipleSearchParams, store]);

  const handleVisibilityChange = useCallback(
    (value) => {
      setIsVisible(value);

      if (!value) {
        clearFilters();
      }
    },
    [clearFilters]
  );

  return {
    isMobile,
    filterDrawer,
    filteredUser,
    inputValue,
    isVisible,
    oldBoard,
    oldDate: oldFrom && (oldDays || oldTo) ? `${oldFrom}-${oldDays || oldTo}` : oldDate,
    openSearchField,
    clearFilters,
    handleApplyFilter,
    handleChange,
    handleOpenSearchField,
    handleVisibilityChange,
    openFilterDrawer: () => setFilterDrawer(true),
    closeFilterDrawer: () => setFilterDrawer(false),
  };
}
